import {
  Button,
  Card,
  Display4,
  Heading3,
  P,
  theme,
} from '@ovotech/element-v5';
import styled from 'styled-components';

export const StyledPlanCard = styled(Card)`
  min-width: 307px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${theme.color.surface.dim};
  padding: 0px;
  border: none;
  outline: none;
`;

// Stretches to the height of the tallest card in the row (see PlanGrid's Col),
// with FeaturesWrapper absorbing the extra space so the grey features box
// grows rather than the white pricing box above it.
export const ContentWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const WhiteCard = styled(Card)`
  flex-grow: 0;
  width: 100%;
  margin: 0;
  padding: 16px;
  border: none;
  outline: none;
`;

export const BannerWrapper = styled.div``;

interface BannerProps {
  backgroundColor: string;
  hasPartnershipImage?: boolean;
}

export const Banner = styled(Card)<BannerProps>`
  height: 120px;
  display: flex;
  padding: ${props => (props.hasPartnershipImage ? '0px' : '0px 100px')};
  align-items: center;
  align-self: stretch;
  justify-content: center;
  width: 100%;
  border-radius: 12px;
  background-color: ${props =>
    props.hasPartnershipImage ? 'transparent' : props.backgroundColor};
  overflow: hidden;
  ${props =>
    props.hasPartnershipImage &&
    `
    border: none;
    box-shadow: none;
  `}
`;

export const FeatureIcon = styled.img<{ hasPartnershipImage?: boolean }>`
  width: ${props => (props.hasPartnershipImage ? '100%' : 'auto')};
  height: ${props => (props.hasPartnershipImage ? '100%' : 'auto')};
  max-height: ${props => (props.hasPartnershipImage ? 'none' : '149px')};
  aspect-ratio: ${props => (props.hasPartnershipImage ? 'unset' : '1/1')};
  object-fit: ${props => (props.hasPartnershipImage ? 'cover' : 'contain')};
`;

export const Body = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  align-self: stretch;
`;

export const StyledHeading3 = styled(Heading3)`
  color: ${theme.color.brand.dark};
  font-weight: 700;
  font-size: 24px !important;
  line-height: 28px !important;
`;

export const PriceGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  padding-top: 16px;
  padding-bottom: 16px;
`;

export const PriceInfoText = styled(P)`
  font-weight: 450;
  color: ${theme.color.surface.onSurfaceVariant};
  font-size: 14px;
`;

export const PlanBodyHeading = styled(P)`
  margin-top: 10px;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  color: ${theme.color.surface.onSurfaceVariant};
`;

export const PriceHeading = styled(P)`
  font-size: 14px;
  font-weight: 450;
  margin-bottom: 0;
  color: ${theme.color.surface.onSurface};
`;

export const FeaturesWrapper = styled.div`
  display: flex;
  flex: 1;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

export const FeaturesHeading = styled(P)`
  font-weight: 700;
  color: ${theme.color.surface.onSurface};
  font-size: 14px;
`;

export const FeaturesList = styled.ul`
  list-style: none;
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  color: #00261c;
`;

export const TickImage = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  margin-top: 2px;
`;

export const FullDetailsWrapper = styled.div`
  display: flex;
  padding: 24px 16px 32px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

export const BadgeContainerSmall = styled.div`
  position: absolute;
  right: 31px;
  top: 122px;
`;

export const BadgeContainerLarge = styled.div`
  position: absolute;
  right: 40px;
  top: 122px;
`;

export const StyledBadge = styled.div`
  display: inline-block;
  color: ${theme.color.alert.info};
  font-weight: 700;
  background-color: ${theme.color.alert.infoTint};
  border-radius: 20px;
  padding: 1px 8px !important;
  border: 2px solid white !important;
`;

// Default CTA style (switch journey, plan details)
export const StyledOutlineButton = styled(Button)`
  border: ${props => (props.variant === 'outline' ? '2px solid #EBE7E3' : '')};
  color: #00261c;
  font-weight: 700;
  &:focus:not(:focus-visible) {
    box-shadow: none;
  }
`;

// Solid CTA (signup journey): dark background with white text
export const SelectPlanButton = styled(Button)`
  background-color: #00261c;
  color: white;
  font-weight: 700;
  border: none;
  width: 100%;

  &:hover {
    background-color: #001f17;
  }

  &:focus-visible {
    outline: 2px solid ${theme.color.surface.link};
    outline-offset: 2px;
  }

  &:focus:not(:focus-visible) {
    box-shadow: none;
  }
`;

export const StyledDisplay4 = styled(Display4)`
  font-size: 48px !important;
  line-height: 1 !important;
`;

export const ViewDetailsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: ${theme.color.surface.link};
  font-size: ${theme.font.size.md};
  line-height: ${theme.font.lineHeight.md};
  font-weight: ${theme.font.weight.book};
  text-decoration: underline;
  margin-top: ${theme.space['600']};
  margin-bottom: calc(-1 * ${theme.space['200']});

  &:focus-visible {
    outline: ${theme.space['50']} solid ${theme.color.surface.link};
    outline-offset: ${theme.space['50']};
  }
`;
